import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  useEffect — Side Effects & Lifecycle
//  Chapter 4 · Hooks
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `import { useEffect } from 'react';

// 1. No dependency array — runs after EVERY render
useEffect(() => {
  document.title = \`You clicked \${count} times\`;
});

// 2. Empty array [] — runs ONCE after mount (like componentDidMount)
useEffect(() => {
  fetchInitialData();
  return () => cleanup();        // cleanup on unmount
}, []);

// 3. Specific deps — runs when those values change
useEffect(() => {
  fetchUser(userId);
}, [userId]);                    // Re-runs whenever userId changes`;

const CODE_2 = `function PostDetail({ postId }) {
  const [post, setPost]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;        // Cleanup flag — prevents stale updates

    async function fetchPost() {
      try {
        setLoading(true);
        setError(null);
        const res  = await fetch(\`/api/posts/\${postId}\`);
        if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
        const data = await res.json();
        if (!cancelled) setPost(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPost();

    // Cleanup — if postId changes before fetch completes, cancel it
    return () => { cancelled = true; };
  }, [postId]);

  if (loading) return <Spinner />;
  if (error)   return <p>Error: {error}</p>;
  return <article><h1>{post.title}</h1><p>{post.body}</p></article>;
}`;

const CODE_3 = `// Timer — cleanup with clearInterval
function Stopwatch() {
  const [time, setTime]     = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;               // Don't start if not running

    const id = setInterval(() => {
      setTime(t => t + 1);
    }, 1000);

    return () => clearInterval(id);     // Cleanup when running changes
  }, [running]);

  return (
    <div>
      <p>{time}s</p>
      <button onClick={() => setRunning(r => !r)}>
        {running ? 'Stop' : 'Start'}
      </button>
      <button onClick={() => { setRunning(false); setTime(0); }}>
        Reset
      </button>
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function UseEffectSideEffectsAndLifecycle() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 4 · Hooks</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>useEffect — Side Effects & Lifecycle</h1>
        <p style={styles.desc}>Control side effects like data fetching, timers, and subscriptions in React.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What is a Side Effect? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What is a Side Effect?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A side effect is anything that happens <i>outside</i> of rendering — fetching data, setting up timers, subscribing to events, manually updating the DOM, or storing to localStorage. useEffect is React&#39;s escape hatch for running this code safely." }} />
      </div>

      /* ── useEffect Syntax ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>useEffect Syntax</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "useEffect takes two arguments: a callback function (the effect) and an optional dependency array." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Data Fetching Pattern ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Data Fetching Pattern</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "The correct pattern for fetching data in useEffect — includes cleanup to prevent state updates on unmounted components." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Timer & Event Listener ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Timer & Event Listener</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Always clean up timers and event listeners to prevent memory leaks." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What are the three ways to use the dependency array?</div>
          <div style={styles.qItem}>✦ 2. Why must you return a cleanup function for subscriptions?</div>
          <div style={styles.qItem}>✦ 3. What is the ESLint exhaustive-deps rule?</div>
          <div style={styles.qItem}>✦ 4. Can you use async directly as the useEffect callback?</div>
          <div style={styles.qItem}>✦ 5. What happens if you omit a dependency from the array?</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
